import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ArrowRight, Sparkles, Zap } from "lucide-react";
import HeroVisual from "@/components/hero/HeroVisual";
import SplitHeading from "@/components/common/SplitHeading";
import MagneticButton from "@/components/common/MagneticButton";
import { useCountUp } from "@/hooks/useCountUp";

const STATS = [
  { type: "count", target: 10, suffix: "×", label: "Faster response" },
  { type: "count", target: 70, suffix: "%", label: "Ops automated" },
  { type: "static", value: "24/7", label: "AI workforce" },
  { type: "static", value: "3-7 days", label: "Go live" },
];

function StatTile({ stat, delay }) {
  const [ref, display] = useCountUp(stat.target || 0, { suffix: stat.suffix || "" });
  const tileRef = useRef(null);

  useEffect(() => {
    if (!tileRef.current) return;
    gsap.fromTo(
      tileRef.current,
      { opacity: 0, y: 24, scale: 0.96 },
      { opacity: 1, y: 0, scale: 1, duration: 0.7, ease: "power3.out", delay }
    );
  }, [delay]);

  return (
    <div
      ref={tileRef}
      className="glass rounded-2xl p-4 text-center border border-[var(--glass-border)] hover:border-[var(--card-hover-border)] transition-colors"
      style={{ opacity: 0 }}
    >
      <div
        ref={stat.type === "count" ? ref : undefined}
        className="text-2xl font-semibold text-[var(--body-fg)] tracking-tight"
      >
        {stat.type === "count" ? display : stat.value}
      </div>
      <div className="text-xs text-[var(--muted-text)] mt-1">{stat.label}</div>
    </div>
  );
}

export default function Hero() {
  const eyebrowRef = useRef(null);
  const subRef = useRef(null);
  const ctaRef = useRef(null);

  useEffect(() => {
    const tl = gsap.timeline({ delay: 0.4 });

    if (eyebrowRef.current) {
      tl.fromTo(
        eyebrowRef.current,
        { opacity: 0, y: 16 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power3.out" },
        0
      );
    }

    if (subRef.current) {
      tl.fromTo(
        subRef.current,
        { opacity: 0, y: 18 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power3.out" },
        0.55
      );
    }

    if (ctaRef.current) {
      tl.fromTo(
        ctaRef.current,
        { opacity: 0, y: 16 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power3.out" },
        0.75
      );
    }

    return () => tl.kill();
  }, []);

  return (
    <section id="top" className="relative pt-36 pb-20 md:pt-44 md:pb-28 overflow-hidden">
      <div className="aurora" />
      <div className="absolute inset-0 grid-bg" />
      <div className="noise" />

      {/* Floating gradient orbs */}
      <div className="pointer-events-none absolute top-1/4 -left-32 w-[28rem] h-[28rem] rounded-full bg-orange-400/10 blur-[100px] animate-glow-pulse" />
      <div className="pointer-events-none absolute bottom-1/4 -right-32 w-[32rem] h-[32rem] rounded-full bg-amber-300/10 blur-[120px] animate-glow-pulse" style={{ animationDelay: "2s" }} />

      <div className="container-x relative z-10">
        <div className="relative max-w-4xl mx-auto text-center">
          <div ref={eyebrowRef} className="flex justify-center" style={{ opacity: 0 }}>
            <span className="eyebrow cursor-pointer hover:bg-orange-500/10 transition-colors" data-testid="hero-eyebrow">
              <Zap className="w-3.5 h-3.5 text-[var(--brand)]" />
              AI Automation Agency · India
            </span>
          </div>

          <SplitHeading
            as="h1"
            className="h-hero mt-8 text-[var(--body-fg)] text-5xl sm:text-6xl md:text-7xl lg:text-8xl"
            data-testid="hero-headline"
            type="words"
            y={70}
            rotateX={-25}
            duration={0.9}
            stagger={0.05}
            start="top 90%"
          >
            Your Business Deserves an{" "}
            <span className="font-serif italic gradient-text">AI Workforce</span>.
          </SplitHeading>

          <p
            ref={subRef}
            className="mx-auto mt-7 max-w-2xl text-base sm:text-lg md:text-xl text-[var(--muted-text)] leading-relaxed"
            data-testid="hero-subheadline"
            style={{ opacity: 0 }}
          >
            We help Small & Medium Businesses automate sales, customer support, lead
            management and operations using AI, WhatsApp automation and intelligent
            business systems.
          </p>

          <div
            ref={ctaRef}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
            style={{ opacity: 0 }}
          >
            <MagneticButton
              as="a"
              href="#booking"
              data-testid="hero-book-btn"
              className="btn-primary"
              strength={0.3}
              scale={1.05}
            >
              <Sparkles className="w-4 h-4" /> Book Free Consultation
            </MagneticButton>
            <MagneticButton
              as="a"
              href="#services"
              data-testid="hero-services-btn"
              className="btn-secondary"
              strength={0.25}
              scale={1.03}
            >
              Explore Services <ArrowRight className="w-4 h-4" />
            </MagneticButton>
          </div>
        </div>

        {/* Stat strip */}
        <div
          className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl mx-auto"
          data-testid="hero-stats"
        >
          {STATS.map((s, i) => (
            <StatTile key={s.label} stat={s} delay={0.9 + i * 0.08} />
          ))}
        </div>

        {/* Dashboard mockup collage */}
        <HeroVisual />
      </div>

      {/* Bottom fade */}
      <div className="pointer-events-none absolute bottom-0 inset-x-0 h-40 bg-gradient-to-b from-transparent to-[var(--body-bg)]" />
    </section>
  );
}
